import { getAuthHeader } from './auth';
import { BACKEND_URL } from '../config';

// The app makes ~173 fetch() calls to its own API and, historically, only 7 of
// them sent an Authorization header. That is why almost every backend route is
// unguarded: adding a guard would have broken the UI.
//
// Rather than edit every call site, install one interceptor that attaches the
// stored token to same-origin /api requests. Requests that already set their
// own Authorization header are left alone, and anything that is not an API call
// (CDNs, third-party endpoints) is passed straight through — the token must not
// leak off-origin.
//
// This is deliberately a no-op for the backend today: unguarded routes ignore
// the header. It exists so that guards can be added server-side without
// locking the interface out.

// Resolved lazily rather than at module scope so importing this file does not
// require a browser (it would otherwise throw under test or SSR).
const getApiOrigin = () => {
  const here = window.location.origin;
  try {
    return BACKEND_URL ? new URL(BACKEND_URL, here).origin : here;
  } catch {
    return here;
  }
};

export const isApiRequest = (rawUrl) => {
  if (!rawUrl) return false;
  try {
    const u = new URL(rawUrl, window.location.origin);
    return u.origin === getApiOrigin() && u.pathname.startsWith('/api');
  } catch {
    return false;
  }
};

export function installAuthFetch() {
  if (typeof window === 'undefined' || window.__signalAuthFetchInstalled) return;
  const originalFetch = window.fetch.bind(window);

  window.fetch = (input, init) => {
    const url = typeof input === 'string' ? input : input && input.url;
    if (!isApiRequest(url)) return originalFetch(input, init);

    const headers = new Headers(
      (init && init.headers) || (typeof input !== 'string' && input ? input.headers : undefined)
    );
    if (!headers.has('Authorization')) {
      const { Authorization } = getAuthHeader();
      if (Authorization) headers.set('Authorization', Authorization);
    }
    return originalFetch(input, { ...(init || {}), headers });
  };

  window.__signalAuthFetchInstalled = true;
}
